/**
 * AuthContext — provides Firebase Auth state + Firestore CMS user profile.
 *
 * Flow:
 *  1. Firebase Auth emits onAuthStateChanged
 *  2. If user signed in → subscribe to /users/{uid} in Firestore (real-time)
 *  3. Expose { firebaseUser, cmsUser, loading, error } to the app
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth';
import {
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import type { CmsUser } from '@/types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuthContextValue {
  firebaseUser: FirebaseUser | null;
  cmsUser:      CmsUser | null;
  loading:      boolean;
  error:        string | null;
  refreshUser:  () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue>({
  firebaseUser: null,
  cmsUser:      null,
  loading:      true,
  error:        null,
  refreshUser:  () => {},
});

// ─── Firestore → CmsUser converter ────────────────────────────────────────────

function docToCmsUser(data: Record<string, unknown>, uid: string): CmsUser {
  const ts = (val: unknown) =>
    val && typeof val === 'object' && 'toDate' in val
      ? (val as { toDate: () => Date }).toDate()
      : null;

  return {
    uid,
    email:         (data.email as string)       ?? '',
    displayName:   (data.displayName as string) ?? '',
    photoURL:      (data.photoURL as string | null) ?? null,
    status:        (data.status as CmsUser['status']) ?? 'pending',
    role:          (data.role   as CmsUser['role'])   ?? 'viewer',
    permissions:   (data.permissions as CmsUser['permissions']) ?? {
      mothers:     { view: false, add: false, edit: false, delete: false, uploadFiles: false, removeFiles: false, copyContent: false, approveContent: false, publishContent: false },
      fathers:     { view: false, add: false, edit: false, delete: false, uploadFiles: false, removeFiles: false, copyContent: false, approveContent: false, publishContent: false },
      grandparents:{ view: false, add: false, edit: false, delete: false, uploadFiles: false, removeFiles: false, copyContent: false, approveContent: false, publishContent: false },
      global:      { viewDashboard: false, manageUsers: false, viewAuditLogs: false },
    },
    createdAt:     ts(data.createdAt),
    approvedAt:    ts(data.approvedAt),
    approvedBy:    (data.approvedBy as string | null) ?? null,
    lastLoginAt:   ts(data.lastLoginAt),
    deactivatedAt: ts(data.deactivatedAt),
    deactivatedBy: (data.deactivatedBy as string | null) ?? null,
  };
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [cmsUser,      setCmsUser]      = useState<CmsUser | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);
  const [refreshKey,   setRefreshKey]   = useState(0);

  const refreshUser = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    let unsubscribeFirestore: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (fbUser: import('firebase/auth').User | null) => {
      // Clean up any existing Firestore listener
      unsubscribeFirestore?.();
      unsubscribeFirestore = null;

      if (!fbUser) {
        setFirebaseUser(null);
        setCmsUser(null);
        setLoading(false);
        setError(null);
        return;
      }

      setFirebaseUser(fbUser);
      setLoading(true);   // hold the spinner until the Firestore snapshot arrives

      // Update lastLoginAt on sign-in
      try {
        await updateDoc(doc(db, 'users', fbUser.uid), {
          lastLoginAt: serverTimestamp(),
        });
      } catch {
        // Doc may not exist yet if Cloud Function is still running — ignore
      }

      // Subscribe to real-time Firestore user doc
      unsubscribeFirestore = onSnapshot(
        doc(db, 'users', fbUser.uid),
        (snap) => {
          if (snap.exists()) {
            const data = snap.data() as Record<string, unknown>;
            console.log('[Auth] Firestore user doc:', JSON.stringify({ status: data.status, role: data.role }));
            setCmsUser(docToCmsUser(data, fbUser.uid));
          } else {
            // Doc not created yet — show loading until Cloud Function fires
            setCmsUser(null);
          }
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Firestore user doc error:', err);
          setError('Failed to load your account. Please refresh.');
          setLoading(false);
        }
      );
    });

    return () => {
      unsubscribeAuth();
      unsubscribeFirestore?.();
    };
  }, [refreshKey]);

  return (
    <AuthContext.Provider value={{ firebaseUser, cmsUser, loading, error, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuthContext(): AuthContextValue {
  return useContext(AuthContext);
}

export { AuthContext };
